import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Container, 
  Dialog, 
  IconButton, 
  Tab, 
  Tabs, 
  Rating
} from '@mui/material';
import { useLanguage } from '../i18n/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

// Import Images
import heroImg from '../imaged/growth-close-up-environmental-lush-natural_1172-349.jpg';
import leafImg from '../imaged/leaf.jpg';
import pr1 from '../imaged/pr1.jpg';
import kaya from '../imaged/Kayachikitsa.jpg';
 

const MotionBox = motion(Box);

const Gallery = () => {
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('All');
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Data Organization: remove existing hardcoded items, include only new home/garden images

  // Dynamically include new "home" and "garden" images placed in src/imaged
  let extraItems = [];
  try {
    const ctx = require.context('../imaged', false, /\.(png|jpe?g|webp)$/i);
    const files = ctx.keys();
    const startId = 1;
    extraItems = files
      .filter((k) => {
        const name = k.toLowerCase();
        // Keep only new home/garden/whatsapp-named photos that aren't already imported
        const isHomeGarden = name.includes('home') || name.includes('garden');
        const isWhatsApp = name.includes('whatsapp image');
        return isHomeGarden || isWhatsApp;
      })
      .map((k, idx) => {
        const lower = k.toLowerCase();
        const category = lower.includes('garden') ? 'Nature' : 'Clinic';
        const size = (idx % 4 === 0) ? 'large' : (idx % 2 === 0 ? 'medium' : 'small');
        return {
          id: startId + idx,
          src: ctx(k),
          category,
          titleKey: category === 'Nature' ? 'Ayurvedic Life' : 'Welcome Area',
          size
        };
      });
  } catch (e) {
    // ignore if context not available
  }

  const GALLERY_ITEMS = extraItems;

  const CATEGORY_KEYS = ['All', 'Our Factory', 'Our Farm'];

  const TESTIMONIALS = [
    { name: 'Anita Sharma', rating: 5, text: t('gallery.testimonials.items.1.text'), role: t('gallery.testimonials.items.1.role'), avatar: pr1 },
    { name: 'Rahul Verma', rating: 5, text: t('gallery.testimonials.items.2.text'), role: t('gallery.testimonials.items.2.role'), avatar: kaya },
    { name: 'Meera Iyer', rating: 4, text: t('gallery.testimonials.items.3.text'), role: t('gallery.testimonials.items.3.role'), avatar: leafImg }
  ];

  const handleOpen = (img) => {
    setSelectedImage(img);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const normalizeCategory = (item) => {
    const n = (item.src || '').toString().toLowerCase();
    if (item.category) return item.category;
    if (n.includes('factory') || n.includes('plant') || n.includes('machin') || n.includes('lab')) return 'Our Factory';
    if (n.includes('farm') || n.includes('cow') || n.includes('field') || n.includes('garden') || n.includes('herb')) return 'Our Farm';
    return 'Our Farm';
  };

  const categorizedItems = GALLERY_ITEMS.map(i => ({ ...i, category: normalizeCategory(i) }));

  const filteredItems = activeTab === 'All' 
    ? categorizedItems 
    : categorizedItems.filter(item => item.category === activeTab);

  return (
    <Box sx={{ overflowX: "hidden", bgcolor: "#fcfcfc" }}>
      {/* ===== HERO SECTION ===== */}
      <Box
        sx={{
          position: "relative",
          height: "85vh",
          minHeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))"
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, px: { xs: 2, md: 8 } }}>
          <MotionBox
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="overline" sx={{ color: "#a5d6a7", letterSpacing: 6, fontWeight: 700, fontSize: "1.2rem", display: "block", mb: 2 }}>
                  {t('gallery.hero.overline')}
                </Typography>
                <Typography variant="h1" sx={{ color: "white", fontWeight: 800, fontSize: { xs: "3.5rem", md: "6rem" }, lineHeight: 1 }}>
                  {t('gallery.hero.titlePart1')} <br />
                  <span style={{ color: "#81c784" }}>{t('gallery.hero.titlePart2')}</span>
                </Typography>
                <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.9)", mt: 4, maxWidth: 600, fontWeight: 300, fontSize: "1.5rem" }}>
                  {t('gallery.hero.subtitle')}
                </Typography>
              </Grid>
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* Removed featured asymmetric image section as requested */}

      {/* ===== FILTERABLE GALLERY ===== */}
      <Box sx={{ bgcolor: "#f5f5f5", py: 12 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            {/* Removed 'Our Collection' heading */}
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)} 
              variant="scrollable" 
              scrollButtons="auto"
              sx={{ 
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': { 
                  textTransform: 'none', 
                  fontSize: '1.1rem', 
                  fontWeight: 600, 
                  color: 'text.secondary',
                  borderRadius: 50,
                  mx: 1,
                  px: 4,
                  py: 1,
                  minHeight: 48,
                  transition: 'all 0.3s',
                  '&.Mui-selected': { 
                    color: 'white', 
                    bgcolor: 'primary.main',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.3)'
                  }
                }
              }}
            >
              {CATEGORY_KEYS.map(cat => (
                <Tab key={cat} label={cat} value={cat} disableRipple />
              ))}
            </Tabs>
          </Box>

          <motion.div layout>
            <Grid container spacing={3}>
              <AnimatePresence>
                {filteredItems.map((item) => (
                  <Grid item xs={12} sm={6} md={item.size === 'large' ? 6 : 3} key={item.id}>
                    <MotionBox
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => handleOpen(item.src)}
                      sx={{ 
                        position: 'relative', 
                        height: item.size === 'large' ? 500 : 350, 
                        borderRadius: 6, 
                        overflow: 'hidden', 
                        cursor: 'pointer',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        '&:hover .overlay': { opacity: 1 },
                        '&:hover img': { transform: 'scale(1.1)' }
                      }}
                    >
                      <img 
                        src={item.src} 
                        alt={t(`gallery.items.${item.titleKey}`)} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }} 
                      />
                      <Box 
                        className="overlay"
                        sx={{ 
                          position: 'absolute', 
                          top: 0, left: 0, right: 0, bottom: 0, 
                          bgcolor: 'rgba(0,0,0,0.4)', 
                          opacity: 0, 
                          transition: 'opacity 0.3s',
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'white',
                          p: 3,
                          textAlign: 'center'
                        }}
                      >
                        <ZoomInIcon sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h5" fontWeight={700}>{t(`gallery.items.${item.titleKey}`)}</Typography>
                        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>{t(`gallery.categories.${item.category}`)}</Typography>
                      </Box>
                    </MotionBox>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ===== TESTIMONIALS REDESIGN ===== */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box textAlign="center" mb={8}>
          <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>
            {t('gallery.testimonials.overline')}
          </Typography>
          <Typography variant="h2" fontWeight={800}>
            {t('gallery.reviewsTitle')}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {TESTIMONIALS.map((tItem, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card 
                sx={{ 
                  height: '100%', 
                  borderRadius: 8, 
                  bgcolor: '#fff',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'visible',
                  mt: 4
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -30, 
                    left: 30, 
                    bgcolor: 'primary.main', 
                    borderRadius: '50%', 
                    width: 60, 
                    height: 60, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.4)'
                  }}
                >
                  <FormatQuoteIcon sx={{ color: 'white', fontSize: 30 }} />
                </Box>
                <CardContent sx={{ pt: 6, px: 4, pb: 4 }}>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
                    "{tItem.text}"
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{tItem.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tItem.role}</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Rating value={tItem.rating} readOnly size="small" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== LIGHTBOX ===== */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="xl"
        PaperProps={{
          style: { backgroundColor: "transparent", boxShadow: "none" }
        }}
      >
        <Box sx={{ position: "relative", outline: "none" }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: -50,
              right: 0,
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(4px)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {selectedImage && (
            <motion.img 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              src={selectedImage} 
              alt="Full view" 
              style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 8, boxShadow: "0 20px 50px rgba(0,0,0,0.5)" }} 
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default Gallery;
